import { Badge } from "components/ui/badge";
import { Skeleton } from "components/ui/skeleton";
import { useConfig } from "hooks/use-config";
import { useGetConversationListInfinite } from "../../../hooks/use-conversation";
import { ConversationSessionSummary } from "../../../types/conversation.model";
import { Conversation } from "../../../types/conversation.service.type";
import { CalendarDays } from "lucide-react";
import { parseAsString, useQueryStates } from "nuqs";
import { useMemo, Dispatch, SetStateAction, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { parseBackendUtc, formatFullDate } from "lib/utils";
import { AgentConversationListFilter } from "./agent-conversation-list-filter";

type PlaygroundFilter = "all" | "playground" | "non-playground";

type AgentConversationListProps = {
  agentId: string;
  setSelectedConversationTitle: (title: string) => void;
  setSelectedConversationUser: (user: string) => void;
  activeTab: "public" | "private";
  setActiveTab: Dispatch<SetStateAction<"public" | "private">>;
};

const ConversationListSkeleton = ({ count = 6 }: { count?: number }) => {
  return (
    <>
      {Array(count)
        .fill(0)
        .map((_, index) => (
          <div
            key={index}
            className="flex cursor-pointer flex-col gap-2 border-b border-gray-200 px-4 py-3"
          >
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-6 w-6 rounded-full" />
            </div>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
    </>
  );
};

type ConversationItemProps = {
  conversation: Conversation;
  isSelected: boolean;
  isLastItem: boolean;
  projectKey: string;
  activeTab: "public" | "private";
  onClick: (conversation: Conversation) => void;
};

const ConversationItem = ({
  conversation,
  isSelected,
  isLastItem,
  projectKey,
  activeTab,
  onClick,
}: ConversationItemProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={`cursor-pointer flex-col items-center justify-between px-4 py-3 ${isSelected ? "rounded-sm bg-surface-app" : "bg-background hover:bg-inherit"
        } ${!isLastItem ? "border-b border-gray-200" : ""}`}
      onClick={() => onClick(conversation)}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 font-semibold">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          <time
            className="text-sm font-semibold dark:text-gray-500"
            dateTime={parseBackendUtc(conversation.createDate).toISOString()}
            title={parseBackendUtc(conversation.createDate).toUTCString()}
          >
            {formatFullDate(new Date(parseBackendUtc(conversation.createDate)))}
          </time>
        </div>
        {conversation.playground && (
          <Badge variant="default" className="w-fit">
            {t('PLAYGROUND')}
          </Badge>
        )}
      </div>
      {activeTab === "private" && (
        <div className="mb-2 mt-2 flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
            {conversation.user_name}
          </span>
        </div>
      )}
      <div className="mb-2 flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold" title={conversation.lastMessage}>
          {conversation.lastMessage}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <time
          className="text-xs text-gray-400 dark:text-gray-500"
          dateTime={parseBackendUtc(conversation.lastUpdated).toISOString()}
          title={parseBackendUtc(conversation.lastUpdated).toUTCString()}
        >
          <span className="pr-1">{t('LAST_UPDATED')}</span>
          {formatFullDate(new Date(parseBackendUtc(conversation.lastUpdated)))}
        </time>
      </div>
    </div>
  );
};

export const AgentConversationList = ({
  agentId,
  setSelectedConversationTitle,
  setSelectedConversationUser,
  activeTab,
  setActiveTab,
}: AgentConversationListProps) => {
  const { t } = useTranslation();
  const [playgroundFilter, setPlaygroundFilter] = useState<PlaygroundFilter>("all");
  const listContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const isPlayground: boolean | null =
    playgroundFilter === "playground" ? true : playgroundFilter === "non-playground" ? false : null;

  const [queryParams, setQueryParams] = useQueryStates({
    widget_id: parseAsString.withDefault(""),
    session_id: parseAsString.withDefault(""),
    start_date: parseAsString.withDefault(""),
    end_date: parseAsString.withDefault(""),
    user_id: parseAsString.withDefault(""),
    user_email: parseAsString.withDefault(""),
    filter_type: parseAsString.withDefault("name"),
    query: parseAsString.withDefault(""),
  });

  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || "";

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetConversationListInfinite({
    agent_id: agentId,
    project_key: projectKey,
    allow_created_by_filter: false,
    is_minimal: true,
    is_private: activeTab === "private",
    query: queryParams.query || null,
    start_date: queryParams.start_date || null,
    end_date: queryParams.end_date || null,
    user_id: queryParams.user_id || null,
    user_email: queryParams.user_email || null,
    pg: isPlayground,
  });

  const handleConversationSelection = (conversation: Conversation) => {
    setQueryParams((prev) => ({
      ...prev,
      widget_id: conversation.widget_id,
      session_id: conversation.sessionId,
    }));
    setSelectedConversationTitle(conversation.lastMessage);
    setSelectedConversationUser(conversation.user_name);
  };

  const conversations: Conversation[] = useMemo(() => {
    const seenIds = new Set<string>();

    return (data?.pages ?? []).flatMap((page) =>
      page.sessions
        .filter((session: ConversationSessionSummary) => {
          if (seenIds.has(session.session_id)) return false;
          seenIds.add(session.session_id);
          return true;
        })
        .map((session: ConversationSessionSummary) => {
          const rawMessage =
            session.conversation.Title ||
            session.conversation.Response ||
            session.conversation.Query ||
            t("NO_MESSAGE");

          return {
            lastMessage: rawMessage,
            lastUpdated: session.last_entry_date,
            createDate: session.created_at,
            widget_id: session.widget_id,
            agentId: session.agent_id,
            sessionId: session.session_id,
            UserId: session.conversation.UserId,
            playground: session.conversation.Playground,
            user_name: session.user_name,
          };
        })
    );
  }, [data?.pages, t]);

  useEffect(() => {
    const setupObserver = () => {
      const loadMoreEl = loadMoreRef.current;
      const scrollContainer = listContainerRef.current;

      if (!loadMoreEl || !scrollContainer) {
        return null;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          });
        },
        {
          root: scrollContainer,
          rootMargin: "200px",
          threshold: 0.1,
        }
      );

      observer.observe(loadMoreEl);
      return observer;
    };

    let observer = setupObserver();
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    if (!observer) {
      [100, 300].forEach((delay) => {
        const timeout = setTimeout(() => {
          if (!observer) {
            observer = setupObserver();
          }
        }, delay);
        timeouts.push(timeout);
      });
    }

    return () => {
      observer?.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, conversations.length, isLoading]);

  const handleTabChange = (tab: "public" | "private") => {
    setActiveTab(tab);
    setPlaygroundFilter("all");
    setQueryParams((prev) => ({
      ...prev,
      session_id: "",
      widget_id: "",
      start_date: "",
      end_date: "",
      user_id: "",
      user_email: "",
      filter_type: "name",
    }));
  };

  return (
    <div className="flex h-full flex-col justify-between overflow-auto rounded-sm bg-background">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => handleTabChange("public")}
          className={`relative flex-1 py-3 text-sm font-medium transition-colors ${activeTab === "public"
            ? "text-blue-600 dark:text-blue-500"
            : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
        >
          {t('PUBLIC')}
          {activeTab === "public" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500" />
          )}
        </button>
        <button
          onClick={() => handleTabChange("private")}
          className={`relative flex-1 py-3 text-sm font-medium transition-colors ${activeTab === "private"
            ? "text-blue-600 dark:text-blue-500"
            : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
        >
          {t('PRIVATE')}
          {activeTab === "private" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500" />
          )}
        </button>
      </div>

      <AgentConversationListFilter
        activeTab={activeTab}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        playgroundFilter={playgroundFilter}
        onPlaygroundFilterChange={(value) => {
          setPlaygroundFilter(value);
        }}
      />

      {/* Conversation List */}
      <div ref={listContainerRef} className="flex-1 overflow-auto p-5">
        <div className="flex flex-col divide-gray-200 overflow-y-auto dark:divide-gray-700">
          {isLoading ? (
            <ConversationListSkeleton />
          ) : (
            <>
              {isError && (
                <div className="p-4 text-center text-red-500">{t("FAILED_TO_LOAD_CONVERSATIONS")}</div>
              )}

              {!isError && conversations.length === 0 && (
                <div className="p-4 text-center text-gray-500">{t("NO_CONVERSATIONS_FOUND")}</div>
              )}

              {conversations.map((conversation, index) => {
                return (
                  <ConversationItem
                    key={conversation.sessionId}
                    conversation={conversation}
                    isSelected={queryParams.session_id === conversation.sessionId}
                    isLastItem={index === conversations.length - 1 && !hasNextPage}
                    projectKey={projectKey}
                    activeTab={activeTab}
                    onClick={handleConversationSelection}
                  />
                );
              })}

              {hasNextPage && <div ref={loadMoreRef} className="h-1 w-full shrink-0" aria-hidden />}

              {isFetchingNextPage && <ConversationListSkeleton count={2} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
